import { Timestamp } from 'firebase/firestore';

export const getDateRange = (range, customStart, customEnd) => {
    const now = new Date();
    let start, end = now;
    const maxLookbackDays = 730; // 2 years - allows prior-year comparisons
    const maxLookbackMs = maxLookbackDays * 24 * 60 * 60 * 1000;
    const earliestAllowedDate = new Date(now.getTime() - maxLookbackMs);

    switch (range) {
        case '7':
            // "Last 7 days" = today + 6 prior days = 7 calendar days ending today.
            // Use (N-1)*24h so start lands on midnight N days ago (not N+1).
            start = new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000);
            break;
        case '14':
            start = new Date(now.getTime() - 13 * 24 * 60 * 60 * 1000);
            break;
        case '30':
            start = new Date(now.getTime() - 29 * 24 * 60 * 60 * 1000);
            break;
        case '90':
            start = new Date(now.getTime() - 89 * 24 * 60 * 60 * 1000);
            break;
        case '180':
            start = new Date(now.getTime() - 179 * 24 * 60 * 60 * 1000);
            break;
        case '365':
            start = new Date(now.getTime() - 364 * 24 * 60 * 60 * 1000);
            break;
        case 'ytd':
            start = new Date(now.getFullYear(), 0, 1); // Jan 1st of current year
            // Cap YTD to 365 days maximum
            if (start < earliestAllowedDate) {
                start = earliestAllowedDate;
            }
            break;
        case 'custom':
            // Parse dates in local timezone to avoid UTC midnight conversion
            if (customStart) {
                const [year, month, day] = customStart.split('-').map(Number);
                start = new Date(year, month - 1, day); // Local timezone
            } else {
                start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            }
            
            if (customEnd) {
                const [year, month, day] = customEnd.split('-').map(Number);
                end = new Date(year, month - 1, day); // Local timezone
            } else {
                end = now;
            }
            
            // Cap custom range to 365 days maximum
            if (start < earliestAllowedDate) {
                start = earliestAllowedDate;
            }
            break;
        default:
            start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    }

    // CRITICAL: Normalize dates to include full days
    // Start date: Set to beginning of day (00:00:00.000)
    start.setHours(0, 0, 0, 0);
    
    // End date: Set to end of day (23:59:59.999)
    // This ensures tasks created on the end date at any time are included
    end.setHours(23, 59, 59, 999);

    return { start, end };
};

/**
 * Validate if a date range exceeds the maximum allowed lookback (365 days)
 * @param {string} range - The range type ('7', '14', '30', '90', '180', 'ytd', 'custom')
 * @param {string} customStart - Custom start date (YYYY-MM-DD)
 * @param {string} customEnd - Custom end date (YYYY-MM-DD)
 * @returns {object} { isValid: boolean, error: string, dayCount: number }
 */
export const validateDateRange = (range, customStart, customEnd) => {
    const maxDays = 730; // 2 years - allows prior-year comparisons
    const now = new Date();
    
    if (range === 'custom') {
        // Require both dates for custom range
        if (!customStart || !customEnd) {
            return {
                isValid: false,
                error: 'Please select both start and end dates for custom range',
                dayCount: 0
            };
        }
        
        const start = new Date(customStart);
        const end = new Date(customEnd);
        
        // Check if end is before start
        if (end < start) {
            return {
                isValid: false,
                error: 'End date must be after start date',
                dayCount: 0
            };
        }
        
        // Calculate day difference
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays > maxDays) {
            return {
                isValid: false,
                error: `Date range cannot exceed ${maxDays} days. Current range: ${diffDays} days.`,
                dayCount: diffDays
            };
        }
    }
    
    // Check YTD range
    if (range === 'ytd') {
        const yearStart = new Date(now.getFullYear(), 0, 1);
        const diffTime = Math.abs(now - yearStart);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays > maxDays) {
            return {
                isValid: false,
                error: `Year to Date range exceeds ${maxDays} days. Please use a custom range or select a shorter period.`,
                dayCount: diffDays
            };
        }
    }
    
    return { isValid: true, error: '', dayCount: 0 };
};

// --- Add this utility for formatting dates according to user preference ---
/**
 * Format a date according to a format string.
 * Supported: 'MM/DD/YYYY', 'MM-DD-YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD'
 * Falls back to 'MM/DD/YYYY' if format is not recognized.
 * @param {Date|string|object} date
 * @param {string} formatStr
 * @returns {string}
 */
export function formatDate(date, formatStr = 'MM/DD/YYYY') {
    if (!date) return '';
    let d = date;
    if (typeof date === 'string') d = new Date(date);
    if (date && typeof date.toDate === 'function') d = date.toDate();
    if (date && date.seconds) d = new Date(date.seconds * 1000);
    if (!(d instanceof Date) || isNaN(d)) return '';

    // Use UTC methods to format dates that were stored at UTC midnight
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');

    switch ((formatStr || '').toUpperCase()) {
        case 'MM-DD-YYYY':
            return `${mm}-${dd}-${yyyy}`;
        case 'DD/MM/YYYY':
            return `${dd}/${mm}/${yyyy}`;
        case 'YYYY-MM-DD':
            return `${yyyy}-${mm}-${dd}`;
        case 'MM/DD/YYYY':
        default:
            return `${mm}/${dd}/${yyyy}`;
    }
}

/**
 * Parse a local date string in the format YYYY-MM-DD or convert Firestore Timestamp to Date.
 * @param {string|Object} dateStr - Date string (YYYY-MM-DD) or Firestore Timestamp
 * @returns {Date|null}
 */
export function parseLocalDate(dateStr) {
    if (!dateStr) return null;
    
    // Handle Firestore Timestamp objects
    if (typeof dateStr === 'object') {
        // Check for .toDate() method (Firestore Timestamp)
        if (dateStr.toDate && typeof dateStr.toDate === 'function') {
            return dateStr.toDate();
        }
        // Check for .seconds property (Timestamp plain object)
        if (dateStr.seconds) {
            return new Date(dateStr.seconds * 1000);
        }
        // If it's already a Date object
        if (dateStr instanceof Date) {
            return dateStr;
        }
    }
    
    // Handle string dates
    if (typeof dateStr === 'string') {
        const [year, month, day] = dateStr.split('-').map(Number);
        // Use Date.UTC to create date at UTC midnight (prevents timezone shifts)
        return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
    }
    
    return null;
}

/**
 * Format a date as MM/DD/YYYY.
 * @param {Date|string|Object} date - Date object, string, or Firestore Timestamp
 * @returns {string}
 */
export function formatDateMMDDYYYY(date) {
    if (!date) return '';
    let d = date;
    if (typeof date === 'string') d = new Date(date);
    if (date && typeof date.toDate === 'function') d = date.toDate();
    if (date && date.seconds) d = new Date(date.seconds * 1000);
    if (!(d instanceof Date) || isNaN(d)) return '';
    // Use UTC methods for dates stored at UTC midnight
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const yyyy = d.getUTCFullYear();
    return `${mm}/${dd}/${yyyy}`;
}

/**
 * Convert MM/DD/YYYY to YYYY-MM-DD
 * @param {string} mmddyyyy
 * @returns {string}
 */
export function mmddyyyyToYyyymmdd(mmddyyyy) {
    if (!mmddyyyy || typeof mmddyyyy !== 'string') return '';
    const [mm, dd, yyyy] = mmddyyyy.split('/');
    if (!mm || !dd || !yyyy) return '';
    return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
}

/**
 * Format a date as YYYY-MM-DD.
 * @param {Date|string|Object} date - Date object, string, or Firestore Timestamp
 * @returns {string}
 */
export function formatDateYYYYMMDD(date) {
    if (!date) return '';
    let d = date;
    if (typeof date === 'string') d = new Date(date);
    if (date && typeof date.toDate === 'function') d = date.toDate();
    if (date && date.seconds) d = new Date(date.seconds * 1000);
    if (!(d instanceof Date) || isNaN(d)) return '';
    // Use UTC methods for dates stored at UTC midnight
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/**
 * Format a date with time according to user preference.
 * @param {Date|string|object} date
 * @param {string} formatStr - Date format (e.g., 'MM/DD/YYYY')
 * @param {boolean} includeTime - Whether to include time (default: true)
 * @returns {string}
 */
export function formatDateTime(date, formatStr = 'MM/DD/YYYY', includeTime = true) {
    if (!date) return '';
    let d = date;
    if (typeof date === 'string') d = new Date(date);
    if (date && typeof date.toDate === 'function') d = date.toDate();
    if (date && date.seconds) d = new Date(date.seconds * 1000);
    if (!(d instanceof Date) || isNaN(d)) return '';

    const dateStr = formatDate(d, formatStr);
    
    if (!includeTime) return dateStr;
    
    // Add time in 12-hour format
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12
    const timeStr = `${hours}:${minutes} ${ampm}`;
    
    return `${dateStr} ${timeStr}`;
}

/**
 * Extract a JavaScript Date from a Firestore Timestamp, string, or Date object.
 * Handles mixed data types gracefully for backwards compatibility.
 * @param {Date|string|object|null} field - The date field to convert
 * @returns {Date|null} JavaScript Date object or null
 */
export function getDateFromField(field) {
    if (!field) return null;
    
    // Firestore Timestamp (has toDate() method)
    if (field.toDate && typeof field.toDate === 'function') {
        return field.toDate();
    }
    
    // Firestore Timestamp (has seconds property)
    if (field.seconds && typeof field.seconds === 'number') {
        return new Date(field.seconds * 1000);
    }
    
    // String or number
    if (typeof field === 'string' || typeof field === 'number') {
        const date = new Date(field);
        return isNaN(date.getTime()) ? null : date;
    }
    
    // Already a Date object
    if (field instanceof Date) {
        return isNaN(field.getTime()) ? null : field;
    }
    
    return null;
}

/**
 * Convert a date string (from input[type="date"]) to a Firestore Timestamp.
 * Use this when saving user-entered dates to Firestore.
 * Stores date at UTC midnight to avoid timezone issues.
 * @param {string|null} dateString - Date string in YYYY-MM-DD format
 * @returns {import('firebase/firestore').Timestamp|null} Firestore Timestamp or null
 */
export function convertDateForSave(dateString) {
    if (!dateString || typeof dateString !== 'string') return null;
    
    const trimmed = dateString.trim();
    if (!trimmed) return null;
    
    try {
        // Parse YYYY-MM-DD and create UTC date at midnight
        const [year, month, day] = trimmed.split('-').map(Number);
        if (!year || !month || !day) return null;
        
        // Create date at UTC midnight (not local midnight)
        const date = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
        
        if (isNaN(date.getTime())) return null;
        
        return Timestamp.fromDate(date);
    } catch (err) {
        console.error('Error converting date for save:', err);
        return null;
    }
}